// HubShell — the container every ClientHub page renders inside (Epic 3 · S3.4).
//
// The shell is the same on every hub page: the hub sidebar with live counts, the
// top bar with the workspace switcher + scope bar, and the page content area.
// Mobile (<768px): sidebar collapses to a drawer; top bar stays. The shell wraps
// the page children — it does NOT know what page is rendering.
//
// This replaces the analytics AdminHeader + NavSidebar that hub pages currently
// use. Existing analytics pages keep their own shell; hub pages use this.

import React, { useState } from "react";
import ThinScroll from "../../shared/ThinScroll";
import HubSidebar from "./HubSidebar";
import HubTopBar from "./HubTopBar";
import ShellSurfaces from "./ShellSurfaces";
import { ShellSurfaceProvider } from "../../../contexts/ShellSurfaceContext";

// `nowCount`, when provided by the Now page, overrides the sidebar's polled Now badge with
// the total of the scope the page is showing (Mine or Everyone); other pages omit it and the
// badge stays the polled personal count.
export default function HubShell({ children, className = "", nowCount }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    // The provider must wrap the WHOLE shell: HubTopBar (and page content) call
    // useShellSurface, so it cannot sit only around <ShellSurfaces/> at the bottom.
    <ShellSurfaceProvider>
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-950">
      {/* Desktop sidebar — always visible ≥768px */}
      <div className="hidden md:flex">
        <HubSidebar nowCount={nowCount} />
      </div>

      {/* Mobile sidebar — drawer, toggled by the hamburger */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute left-0 top-0 h-full">
            <HubSidebar nowCount={nowCount} />
          </div>
        </div>
      )}

      {/* Main column */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <HubTopBar />

        {/* Mobile hamburger — visible only <768px */}
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="absolute left-2 top-2 z-40 rounded-lg p-2 text-gray-600 dark:text-gray-400 md:hidden"
          aria-label="Open navigation"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        <ThinScroll
          as="main"
          className={`flex-1 overflow-y-auto ${className}`}
          data-testid="hub-content"
        >
          {children}
        </ThinScroll>
      </div>

      {/* Shell-level surfaces (Epic 4) — inbox drawer, timer widget, global
          search. Mounted inside the ShellSurfaceProvider so the top-bar triggers
          and pages can open them; the timer itself lives in the app-root
          TimerProvider so it persists across hub navigation. */}
      <ShellSurfaces />
    </div>
    </ShellSurfaceProvider>
  );
}
